import { getSupabaseClient } from "@veorest/api-client";

export type PartnerFeeBand = {
  min_km: number;
  max_km: number;
  fee_amount: number;
  /** Per-band driver pay (IQD). null = legacy flat pay fallback. */
  driver_amount: number | null;
  /** fee_amount − driver_amount (server-computed, read-only). */
  margin?: number | null;
  sort_order?: number;
};

function mapBands(data: unknown): PartnerFeeBand[] {
  // RPC returns { bands: [...] }; tolerate a bare array too.
  const raw: unknown[] = Array.isArray(data)
    ? data
    : (((data as Record<string, unknown> | null)?.["bands"] as
        | unknown[]
        | undefined) ?? []);
  if (!Array.isArray(raw)) return [];
  return raw.map((rawBand) => {
    const row = (rawBand ?? {}) as Record<string, unknown>;
    const driver =
      row.driver_amount == null || row.driver_amount === ""
        ? null
        : Number(row.driver_amount);
    return {
      min_km: Number(row.min_km ?? 0),
      max_km: Number(row.max_km ?? 0),
      fee_amount: Number(row.fee_amount ?? 0),
      driver_amount: driver != null && Number.isFinite(driver) ? driver : null,
      margin: row.margin == null ? null : Number(row.margin),
      sort_order: Number(row.sort_order ?? 0),
    };
  });
}

/** Partner-owned delivery bands (same RPCs the mobile partner app uses). */
export async function listPartnerFeeBands(partnerId: string): Promise<PartnerFeeBand[]> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("partner_list_fee_bands", {
    p_partner_id: partnerId,
  });
  if (error) throw new Error(error.message);
  return mapBands(data);
}

export async function upsertPartnerFeeBands(
  partnerId: string,
  bands: PartnerFeeBand[]
): Promise<PartnerFeeBand[]> {
  const supabase = getSupabaseClient();
  const payload = bands.map((band, index) => ({
    min_km: band.min_km,
    max_km: band.max_km,
    fee_amount: band.fee_amount,
    driver_amount: band.driver_amount,
    sort_order: index,
  }));
  const { data, error } = await supabase.rpc("partner_upsert_fee_bands", {
    p_partner_id: partnerId,
    p_bands: payload,
  });
  if (error) throw new Error(error.message);
  return mapBands(data);
}
